import { z } from 'zod';
import { ESPECIALIDADES } from '../catalogos/especialidades.js';
import { ESTADOS_DE_INFORMACION, LATERALIDADES } from '../catalogos/nucleo.js';
import { CODIGOS_DE_OBSERVACION, DEFINICION_DE_OBSERVACION, UNIDADES, } from '../catalogos/observaciones.js';
/**
 * Plantillas de formulario clinico (F2.5, plan maestro §6.4).
 *
 * Una plantilla es DATOS: secciones y campos de tipos cerrados, con condiciones
 * en un lenguaje declarativo limitado. No hay `eval`, ni consultas, ni scripts:
 * una condicion compara el valor de otro campo y nada mas. El servidor valida
 * la plantilla entera y sus limites antes de aceptarla.
 *
 * Cada campo tiene un identificador ESTABLE (ASCII). Cambiar la etiqueta no
 * cambia el significado del dato historico; cambiar tipo, unidad u opciones es
 * otra version de la plantilla.
 */
// --- Limites -----------------------------------------------------------------
export const LIMITES_DE_PLANTILLA = {
    secciones: 30,
    camposTotales: 200,
    camposPorSeccion: 60,
    opcionesPorCampo: 100,
    columnasPorTabla: 12,
    filasPorTabla: 100,
    profundidadDeCondicion: 3,
    /** Tamano de la plantilla serializada, en caracteres. */
    tamano: 64_000,
};
/** Identificador estable de campo o seccion: camelCase ASCII. */
const PATRON_IDENTIFICADOR = /^[a-z][a-zA-Z0-9]{0,39}$/;
const esquemaIdentificador = z
    .string()
    .regex(PATRON_IDENTIFICADOR, 'Identificador en camelCase ASCII, hasta 40 caracteres');
/** Identificador de plantilla: `especialidad.nombre` (p. ej. `cardiologia.consulta`). */
export const PATRON_ID_PLANTILLA = /^[a-z]+(?:-[a-z]+)*\.[a-z]+(?:-[a-z]+)*$/;
// --- Condiciones declarativas -----------------------------------------------
const valorComparable = z.union([z.string().max(200), z.number().finite(), z.boolean()]);
export const esquemaCondicion = z.lazy(() => z.union([
    z
        .object({
        op: z.enum(['igual', 'distinto']),
        campo: esquemaIdentificador,
        valor: valorComparable,
    })
        .strict(),
    z
        .object({
        op: z.literal('en'),
        campo: esquemaIdentificador,
        valores: z
            .array(z.union([z.string().max(200), z.number().finite()]))
            .min(1)
            .max(50),
    })
        .strict(),
    z
        .object({
        op: z.literal('contiene'),
        campo: esquemaIdentificador,
        valor: z.string().max(200),
    })
        .strict(),
    z.object({ op: z.enum(['presente', 'ausente']), campo: esquemaIdentificador }).strict(),
    z
        .object({
        op: z.enum(['mayor', 'menor']),
        campo: esquemaIdentificador,
        valor: z.number().finite(),
    })
        .strict(),
    z
        .object({ op: z.enum(['y', 'o']), condiciones: z.array(esquemaCondicion).min(1).max(10) })
        .strict(),
    z.object({ op: z.literal('no'), condicion: esquemaCondicion }).strict(),
]));
export function profundidadDeCondicion(condicion) {
    if (condicion.op === 'y' || condicion.op === 'o') {
        return 1 + Math.max(...condicion.condiciones.map(profundidadDeCondicion));
    }
    if (condicion.op === 'no')
        return 1 + profundidadDeCondicion(condicion.condicion);
    return 1;
}
export function camposDeCondicion(condicion) {
    if ('condiciones' in condicion)
        return condicion.condiciones.flatMap(camposDeCondicion);
    if ('condicion' in condicion)
        return camposDeCondicion(condicion.condicion);
    return [condicion.campo];
}
const estaVacio = (valor) => valor === undefined ||
    valor === null ||
    valor === '' ||
    (Array.isArray(valor) && valor.length === 0);
/**
 * Evalua una condicion contra las respuestas. Funcion pura y total: la misma en
 * el servidor y en la interfaz, sin acceso a nada mas que `respuestas`.
 */
export function evaluarCondicion(condicion, respuestas) {
    switch (condicion.op) {
        case 'igual':
            return respuestas[condicion.campo] === condicion.valor;
        case 'distinto':
            return respuestas[condicion.campo] !== condicion.valor;
        case 'en':
            return condicion.valores.includes(respuestas[condicion.campo]);
        case 'contiene': {
            const valor = respuestas[condicion.campo];
            return Array.isArray(valor) && valor.includes(condicion.valor);
        }
        case 'presente':
            return !estaVacio(respuestas[condicion.campo]);
        case 'ausente':
            return estaVacio(respuestas[condicion.campo]);
        case 'mayor':
        case 'menor': {
            const valor = respuestas[condicion.campo];
            if (typeof valor !== 'number')
                return false;
            return condicion.op === 'mayor' ? valor > condicion.valor : valor < condicion.valor;
        }
        case 'y':
            return condicion.condiciones.every((c) => evaluarCondicion(c, respuestas));
        case 'o':
            return condicion.condiciones.some((c) => evaluarCondicion(c, respuestas));
        case 'no':
            return !evaluarCondicion(condicion.condicion, respuestas);
    }
}
// --- Campos ------------------------------------------------------------------
export const TIPOS_DE_CAMPO = [
    'texto',
    'texto-largo',
    'numero',
    'fecha',
    'booleano',
    'seleccion',
    'seleccion-multiple',
    'estado-informacion',
    'observacion',
    'tabla',
    'adjunto',
];
const esquemaOpcion = z
    .object({
    valor: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Valor de opción en ASCII con guiones'),
    etiqueta: z.string().trim().min(1).max(120),
})
    .strict();
const base = {
    id: esquemaIdentificador,
    etiqueta: z.string().trim().min(1).max(200),
    ayuda: z.string().trim().max(500).optional(),
    /** Si el documento no se firma sin este campo (la captura en borrador si se admite). */
    requeridoParaFirmar: z.boolean().default(false),
    /** Se muestra y se valida solo si la condicion se cumple. */
    condicion: esquemaCondicion.optional(),
    /** Codigo clinico que da significado al campo, si lo hay. */
    semantica: z
        .object({ sistema: z.string().trim().min(1).max(40), codigo: z.string().trim().min(1).max(40) })
        .strict()
        .optional(),
};
const campoTexto = z
    .object({
    ...base,
    tipo: z.literal('texto'),
    maxLongitud: z.number().int().min(1).max(500).default(200),
})
    .strict();
const campoTextoLargo = z
    .object({
    ...base,
    tipo: z.literal('texto-largo'),
    maxLongitud: z.number().int().min(1).max(10_000).default(2000),
})
    .strict();
const campoNumero = z
    .object({
    ...base,
    tipo: z.literal('numero'),
    unidad: z.enum(UNIDADES).optional(),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    decimales: z.number().int().min(0).max(4).default(0),
})
    .strict()
    .refine((c) => c.min === undefined || c.max === undefined || c.min <= c.max, {
    message: 'El mínimo no puede ser mayor que el máximo',
});
const campoFecha = z.object({ ...base, tipo: z.literal('fecha') }).strict();
const campoBooleano = z.object({ ...base, tipo: z.literal('booleano') }).strict();
const campoSeleccion = z
    .object({
    ...base,
    tipo: z.enum(['seleccion', 'seleccion-multiple']),
    opciones: z.array(esquemaOpcion).min(2).max(LIMITES_DE_PLANTILLA.opcionesPorCampo),
})
    .strict();
/** «Refiere / niega / desconocido / no interrogado / no aplica» como valores distintos. */
const campoEstadoInformacion = z
    .object({
    ...base,
    tipo: z.literal('estado-informacion'),
    admite: z
        .array(z.enum(ESTADOS_DE_INFORMACION))
        .min(2)
        .default([...ESTADOS_DE_INFORMACION]),
})
    .strict();
/**
 * Una medicion del catalogo: unidad y lateralidad salen de alli, no de la
 * plantilla. `lado` fija el lado del campo (F6.1): «PIO del ojo derecho» es un
 * campo y «PIO del ojo izquierdo» es otro, y ninguna copia los cruza.
 */
const campoObservacion = z
    .object({
    ...base,
    tipo: z.literal('observacion'),
    codigo: z.enum(CODIGOS_DE_OBSERVACION),
    lado: z.enum(LATERALIDADES).optional(),
})
    .strict();
const campoAdjunto = z
    .object({
    ...base,
    tipo: z.literal('adjunto'),
    maximo: z.number().int().min(1).max(20).default(1),
})
    .strict();
/** Columnas de una tabla: campos simples, sin tablas anidadas ni adjuntos. */
const esquemaColumna = z.union([
    campoTexto,
    campoNumero,
    campoFecha,
    campoBooleano,
    campoSeleccion,
]);
const campoTabla = z
    .object({
    ...base,
    tipo: z.literal('tabla'),
    columnas: z.array(esquemaColumna).min(1).max(LIMITES_DE_PLANTILLA.columnasPorTabla),
    maxFilas: z.number().int().min(1).max(LIMITES_DE_PLANTILLA.filasPorTabla).default(20),
})
    .strict();
export const esquemaCampo = z.union([
    campoTexto,
    campoTextoLargo,
    campoNumero,
    campoFecha,
    campoBooleano,
    campoSeleccion,
    campoEstadoInformacion,
    campoObservacion,
    campoTabla,
    campoAdjunto,
]);
const esquemaSeccion = z
    .object({
    id: esquemaIdentificador,
    titulo: z.string().trim().min(1).max(200),
    condicion: esquemaCondicion.optional(),
    campos: z.array(esquemaCampo).min(1).max(LIMITES_DE_PLANTILLA.camposPorSeccion),
})
    .strict();
// --- Plantilla -----------------------------------------------------------------
export const ESTADOS_DE_PLANTILLA = ['borrador', 'revision', 'publicada', 'retirada'];
/**
 * Ciclo de vida (plan §6.4). Publicada es inmutable: se clona una version nueva
 * para cambiarla. Retirar impide capturas nuevas, no borra historia.
 */
export const TRANSICIONES_DE_PLANTILLA = {
    borrador: ['revision'],
    revision: ['borrador', 'publicada'],
    publicada: ['retirada'],
    retirada: [],
};
/** Version del formato de plantilla. Sube si cambia la forma de este esquema. */
export const VERSION_ESQUEMA_PLANTILLA = 1;
const esquemaPlantillaBase = z
    .object({
    id: z.string().regex(PATRON_ID_PLANTILLA, 'Use especialidad.nombre en ASCII'),
    version: z.number().int().min(1),
    schemaVersion: z.literal(VERSION_ESQUEMA_PLANTILLA),
    especialidad: z.enum(ESPECIALIDADES),
    titulo: z.string().trim().min(1).max(200),
    secciones: z.array(esquemaSeccion).min(1).max(LIMITES_DE_PLANTILLA.secciones),
})
    .strict();
function conReglas(esquema) {
    return esquema.superRefine((plantilla, ctx) => {
        for (const problema of problemasDePlantilla(plantilla)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: problema });
        }
    });
}
export const esquemaPlantilla = conReglas(esquemaPlantillaBase);
/** Plantilla nueva de un consultorio: la version la asigna el servidor. */
export const esquemaNuevaPlantilla = conReglas(esquemaPlantillaBase.omit({ version: true }));
/**
 * Reglas que cruzan campos: identificadores unicos, condiciones que solo
 * miran campos que existen (y no a si mismos), profundidad, cantidad y tamano.
 */
export function problemasDePlantilla(plantilla) {
    const problemas = [];
    if (!plantilla.id.startsWith(`${plantilla.especialidad}.`)) {
        problemas.push('El identificador de la plantilla empieza por su especialidad');
    }
    const campos = plantilla.secciones.flatMap((seccion) => seccion.campos);
    if (campos.length > LIMITES_DE_PLANTILLA.camposTotales) {
        problemas.push(`Demasiados campos: ${campos.length} de ${LIMITES_DE_PLANTILLA.camposTotales}`);
    }
    const vistos = new Set();
    for (const id of [...plantilla.secciones.map((s) => s.id), ...campos.map((c) => c.id)]) {
        if (vistos.has(id))
            problemas.push(`Identificador repetido: ${id}`);
        vistos.add(id);
    }
    const idsDeCampo = new Set(campos.map((c) => c.id));
    const revisar = (condicion, duenio) => {
        if (!condicion)
            return;
        if (profundidadDeCondicion(condicion) > LIMITES_DE_PLANTILLA.profundidadDeCondicion) {
            problemas.push(`La condición de ${duenio} es demasiado profunda`);
        }
        for (const referido of camposDeCondicion(condicion)) {
            if (!idsDeCampo.has(referido))
                problemas.push(`La condición de ${duenio} usa un campo inexistente: ${referido}`);
            if (referido === duenio)
                problemas.push(`La condición de ${duenio} depende de sí mismo`);
        }
    };
    for (const seccion of plantilla.secciones) {
        revisar(seccion.condicion, seccion.id);
        for (const campo of seccion.campos)
            revisar(campo.condicion, campo.id);
    }
    for (const campo of campos) {
        if (campo.tipo !== 'observacion' || campo.lado === undefined)
            continue;
        const definicion = DEFINICION_DE_OBSERVACION[campo.codigo];
        if (definicion.lateralidad !== 'requerida') {
            problemas.push(`El campo ${campo.id} fija un lado, pero ${definicion.nombre} no lleva lado`);
        }
        else if (definicion.lados && !definicion.lados.includes(campo.lado)) {
            problemas.push(`El campo ${campo.id} fija un lado que ${definicion.nombre} no admite`);
        }
    }
    const tamano = JSON.stringify(plantilla).length;
    if (tamano > LIMITES_DE_PLANTILLA.tamano) {
        problemas.push(`La plantilla es demasiado grande: ${tamano} de ${LIMITES_DE_PLANTILLA.tamano} caracteres`);
    }
    return problemas;
}
/** Referencia a una version concreta: `cardiologia.consulta.v1`. */
export function referenciaDePlantilla(plantilla) {
    return `${plantilla.id}.v${plantilla.version}`;
}
export const esquemaCambiarEstadoPlantilla = z
    .object({
    estado: z.enum(ESTADOS_DE_PLANTILLA),
    motivo: z.string().trim().min(5).max(300).optional(),
})
    .strict()
    .refine((c) => c.estado !== 'retirada' || c.motivo !== undefined, {
    message: 'Retirar una plantilla exige motivo',
    path: ['motivo'],
});
/** Copiar al consultorio una plantilla de un paquete (`cardiologia.consulta.v1`). */
export const esquemaImportarPlantilla = z
    .object({ referencia: z.string().regex(/^[a-z]+(?:-[a-z]+)*\.[a-z]+(?:-[a-z]+)*\.v[1-9]\d*$/) })
    .strict();
//# sourceMappingURL=plantilla.js.map